/**
 * Outbrain Pixel — self-registering.
 */

import { warn } from '../../util/log';
import type { OutbrainPixel } from '../../core/config/types';
import { on, SdkEvent } from '../../core/events/bus';
import type { PixelRegister } from './types';

declare global {
  interface Window {
    obApi?: ((...args: unknown[]) => void) & {
      marketerId?: string;
      queue?: unknown[][];
      version?: string;
    };
  }
}

const SCRIPT_URL = 'https://amplify.outbrain.com/cp/obtp.js';
let scriptInjected = false;

function ensureObApi(marketerId: string): void {
  if (typeof window === 'undefined') return;
  const w = window as Window & { obApi?: typeof window.obApi };
  if (typeof w.obApi !== 'function') {
    const stub = function (...args: unknown[]): void {
      const fn = stub as unknown as { queue: unknown[][] };
      fn.queue.push(args);
    } as unknown as Window['obApi'];
    (stub as unknown as { queue: unknown[][]; version: string; marketerId: string }).queue = [];
    (stub as unknown as { version: string }).version = '1.1';
    (stub as unknown as { marketerId: string }).marketerId = marketerId;
    w.obApi = stub;
  } else {
    w.obApi.marketerId = marketerId;
  }
  if (scriptInjected) return;
  scriptInjected = true;
  try {
    const s = document.createElement('script');
    s.async = true;
    s.src = SCRIPT_URL;
    (document.head || document.documentElement).appendChild(s);
  } catch (err) {
    warn('outbrain script inject failed', err);
  }
}

// NOTE: `obApi` holds a single global `marketerId` — Outbrain's tag API has no
// per-pixel scoping, so the last-registered Outbrain pixel wins on this page.
export const registerOutbrain: PixelRegister<OutbrainPixel> = (pixel, context) => {
  on(SdkEvent.PAGE_VIEW, () => {
    if (context.getConsent() !== 'granted') return;
    try {
      ensureObApi(pixel.pixel_id);
      window.obApi!('track', 'PAGE_VIEW');
    } catch (err) {
      warn('outbrain pageview failed', err);
    }
  });

  on(SdkEvent.ECOM_CLICKOUT, () => {
    if (context.getConsent() !== 'granted') return;
    try {
      window.obApi?.('track', 'EcomClickOut');
    } catch (err) {
      warn('outbrain clickout failed', err);
    }
  });

  // any milestone ≤50, quarter-cents included (yoto parity)
  on(SdkEvent.SESSION_BID_CENT_INCREMENT, (cents) => {
    if (context.getConsent() !== 'granted') return;
    if (cents <= 0 || cents > 50) return;
    try {
      ensureObApi(pixel.pixel_id);
      window.obApi!('track', `${cents} cent`);
    } catch (err) {
      warn('outbrain cent event failed', err);
    }
  });
};

export function _resetOutbrainForTests(): void {
  scriptInjected = false;
}
